import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import {
  FieldValues,
  Operation,
  OperationProfile,
  TemplatePullPreviewResponse,
  TemplatePullRequest,
} from '../models/operation-profile.model';
import { Modal } from '../../../shared/components/modal/modal';
import { Icon } from '../../../shared/components/icon/icon';

/** Which side of a conflict the operator chose to keep. */
type Choice = 'mine' | 'theirs';

/**
 * A row in the change list. 'auto' changes are applied as-is (template only
 * changed them) and shown read-only as mine → theirs. 'conflict' changes were
 * edited on both sides and carry the operator's current pick.
 */
interface ChangeRow {
  values: FieldValues;
  kind: 'auto' | 'conflict';
  /** Only meaningful for conflicts; defaults to 'theirs'. */
  choice: Choice;
}

/**
 * Template-pull resolution modal. The parent fetches the preview and hands it
 * in; this component previews every incoming change and lets the operator
 * resolve conflicts:
 *  - auto changes (template-only) → read-only "mine → theirs" rows.
 *  - conflicts (both edited) → a two-way choice (keep yours / take template),
 *    template pre-selected.
 *  - no changes at all → a short "nothing to pull" summary.
 *
 * If the backend hasn't populated `changes` yet, it falls back to the
 * `conflicts` array alone (conflicts still resolve; auto changes just aren't
 * listed).
 *
 * On confirm it emits the full TemplatePullRequest: the profile's current 8
 * settings, with each conflicting field overridden by the chosen value, plus
 * the preview's toVersion as the optimistic guard. Operations are not included
 * (the server replaces them from the template).
 */
@Component({
  selector: 'app-template-pull-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, Modal, Icon],
  templateUrl: './template-pull-dialog.component.html',
})
export class TemplatePullDialogComponent {
  private readonly transloco = inject(TranslocoService);

  /** The profile being updated — source of `mine` for non-conflicting fields. */
  readonly profile = input.required<OperationProfile>();
  /** The preview result (computed by the parent). */
  readonly preview = input.required<TemplatePullPreviewResponse>();
  /** Freezes the footer while the apply request is in flight. */
  readonly busy = input(false);

  readonly open = model(true);
  readonly confirm = output<TemplatePullRequest>();
  readonly close = output<void>();

  /** User overrides keyed by field; empty until the operator clicks a card. */
  private readonly picks = signal<Record<string, Choice>>({});

  /**
   * Every incoming change, classified. Prefers the backend's `changes` list
   * (full diff); falls back to `conflicts` alone when it's absent. A change is
   * a conflict iff its field is in `conflicts`. Conflicts seed to 'theirs' and
   * take any pick the operator has made this session.
   */
  readonly rows = computed<ChangeRow[]>(() => {
    const preview = this.preview();
    const conflictFields = new Set(preview.conflicts.map((c) => c.field));
    const source = preview.changes?.length ? preview.changes : preview.conflicts;
    const picks = this.picks();
    return source.map((values) => {
      const isConflict = conflictFields.has(values.field);
      return {
        values,
        kind: isConflict ? 'conflict' : 'auto',
        choice: picks[values.field] ?? 'theirs',
      } as ChangeRow;
    });
  });

  /** Conflict rows — the ones needing an explicit choice. */
  readonly conflictRows = computed(() => this.rows().filter((r) => r.kind === 'conflict'));
  /** Auto-applied rows — template-only changes, shown read-only. */
  readonly autoRows = computed(() => this.rows().filter((r) => r.kind === 'auto'));

  readonly hasConflicts = computed(() => this.conflictRows().length > 0);

  // ── Operation replacement (informational — no per-op choice) ─────────────

  /** The backend's operation diff, if present. */
  readonly opsDiff = computed(() => this.preview().operations ?? null);
  /** Whether the pull replaces operations with a different set. */
  readonly opsChanged = computed(() => this.opsDiff()?.changed ?? false);
  readonly currentOps = computed(() => this.opsDiff()?.current ?? []);
  readonly incomingOps = computed(() => this.opsDiff()?.incoming ?? []);

  /** Any change at all — a scalar setting change OR an operation change. Drives
   *  the "nothing to pull" fallback. */
  readonly hasAnyChange = computed(() => this.rows().length > 0 || this.opsChanged());

  choose(field: string, choice: Choice): void {
    this.picks.update((picks) => ({ ...picks, [field]: choice }));
  }

  isChosen(field: string, choice: Choice): boolean {
    return this.rows().find((r) => r.values.field === field)?.choice === choice;
  }

  // ── Labelling / formatting ───────────────────────────────────────────────

  fieldLabel(field: string): string {
    const key = `operations.templatePull.fields.${field}`;
    const label = this.transloco.translate(key);
    // Fall back to the raw key name if a field isn't in the dictionary.
    return label === key ? field : label;
  }

  /** Human-format a conflict value by field type. Booleans → On/Off,
   *  intervals (seconds) → "45 s" / "2 min", percentages → "20%". */
  formatValue(field: string, value: string | number | boolean | null): string {
    if (value == null) return this.transloco.translate('operations.na');
    if (typeof value === 'boolean') {
      return value
        ? this.transloco.translate('operations.on')
        : this.transloco.translate('operations.off');
    }
    if (field === 'renderInterval' || field === 'connectionInterval') {
      return this.formatInterval(value as number);
    }
    if (field.toLowerCase().includes('percentage')) {
      return `${value}%`;
    }
    return String(value);
  }

  private formatInterval(seconds: number): string {
    if (seconds < 60) return `${seconds} s`;
    const m = seconds / 60;
    return `${Number.isInteger(m) ? m : m.toFixed(1)} min`;
  }

  baseLabel(c: FieldValues): string {
    return this.transloco.translate('operations.templatePull.base', {
      value: this.formatValue(c.field, c.base),
    });
  }

  /** One-line schedule summary for an operation row in the ops diff:
   *  "Mon–Fri · 05:00–23:00", or a sleep/shutdown note. Kept intentionally
   *  terse — this is a preview, not the full operation inspector. */
  operationSummary(op: Operation): string {
    const t = (k: string) => this.transloco.translate(`operations.${k}`);
    if (op.shutdown) return t('flags.shutdown');
    if (op.sleep) return t('flags.sleep');
    const days = op.days?.length ? op.days.map((d) => d.slice(0, 3)).join(', ') : t('na');
    const window =
      op.startTime && op.endTime ? `${op.startTime}–${op.endTime}` : t('na');
    return `${days} · ${window}`;
  }

  // ── Confirm / close ──────────────────────────────────────────────────────

  onConfirm(): void {
    this.confirm.emit(this.buildRequest());
  }

  onClose(): void {
    this.close.emit();
  }

  /**
   * Assemble the resolved settings: start from the profile's current values
   * (mine), then for every change row apply its resolved side — auto rows take
   * 'theirs' (template-only change), conflict rows take the operator's pick.
   * Fields with no change row keep mine. The server recomputes the merge, and
   * our values are authoritative for the 8 settings per the API contract.
   */
  private buildRequest(): TemplatePullRequest {
    const p = this.profile();
    const rows = new Map(this.rows().map((r) => [r.values.field, r] as const));

    const resolve = (field: string, mine: unknown): unknown => {
      const r = rows.get(field);
      if (!r) return mine;
      return r.choice === 'theirs' ? r.values.theirs : r.values.mine;
    };

    return {
      toVersion: this.preview().toVersion,
      powerMode: resolve('powerMode', p.powerMode) as string | null,
      lowPowerPercentage: resolve('lowPowerPercentage', p.lowPowerPercentage) as number | null,
      lowPowerPercentageOut: resolve('lowPowerPercentageOut', p.lowPowerPercentageOut) as number | null,
      noPowerPercentage: resolve('noPowerPercentage', p.noPowerPercentage) as number | null,
      noPowerPercentageOut: resolve('noPowerPercentageOut', p.noPowerPercentageOut) as number | null,
      renderInterval: resolve('renderInterval', p.renderInterval) as number | null,
      connectionInterval: resolve('connectionInterval', p.connectionInterval) as number | null,
      allowWakeup: resolve('allowWakeup', p.allowWakeup) as boolean | null,
    };
  }
}
